import { DEFAULT_LOCALE } from "~/utils/localization";

export const formatMoney = (
  value: number,
  currency: string = "$",
  locale: string = DEFAULT_LOCALE,
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 2,
): string => {
  // Handle invalid input
  if (value === null || value === undefined || isNaN(value)) {
    return `${currency}0.00`;
  }

  // If both fraction digits are 0, round down to nearest dollar
  let numericValue = value;
  if (minimumFractionDigits === 0 && maximumFractionDigits === 0) {
    numericValue = Math.floor(value);
  }

  // Format the number using Intl.NumberFormat
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${currency}${formatter.format(numericValue)}`;
};

/**
 * Simple money formatter with default USD formatting
 * @param value - The value to format
 * @returns Formatted money string with $ symbol
 */
export const toMoney = (value: number): string => {
  return formatMoney(value);
};

/**
 * Formats money without currency symbol
 * @param value - The value to format
 * @returns Formatted number string
 */
export const formatMoneyAmount = (value: number): string => {
  return formatMoney(value, "");
};

/**
 * Parses a money string back to a number
 * @param moneyString - The formatted money string
 * @returns Numeric value
 */
export const parseMoney = (moneyString: string): number => {
  const numericValue = parseFloat(moneyString.replace(/[^0-9.-]/g, ""));
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Converts a money string to cents (integer value)
 * @param moneyString - The formatted money string (e.g., "$12.34", "1,234.56")
 * @returns Integer value in cents
 */
export const moneyToCents = (moneyString: string): number => {
  // Handle empty or invalid input
  if (!moneyString || typeof moneyString !== "string") {
    return 0;
  }

  // Remove all non-numeric characters except decimal point and minus sign
  const cleanedString = moneyString.replace(/[^0-9.-]/g, "");

  // Parse to float
  const dollarValue = parseFloat(cleanedString);

  // Handle invalid numbers
  if (isNaN(dollarValue)) {
    return 0;
  }

  // Convert to cents and round to handle floating point precision issues
  return Math.round(dollarValue * 100);
};

/**
 * Converts cents (integer) back to money string
 * @param cents - Integer value in cents
 * @param currency - Currency symbol (default: '$')
 * @param locale - Locale for formatting (default: 'en-US')
 * @param showCents - Whether to show cents in the result (default: true)
 * @returns Formatted money string
 */
export const centsToMoney = (
  cents: number,
  currency: string = "$",
  locale: string = DEFAULT_LOCALE,
  showCents: boolean = true,
): string => {
  // Handle invalid input
  if (typeof cents !== "number" || isNaN(cents)) {
    return formatMoney(0, currency, locale);
  }

  // Convert cents to dollars
  const dollarValue = cents / 100;

  // Use formatMoney for consistent formatting
  if (showCents) {
    return formatMoney(dollarValue, currency, locale);
  } else {
    return formatMoney(dollarValue, currency, locale, 0, 0);
  }
};

export const getPayoutAmountAfterFee = (cents: number): number => {
  // Handle invalid input
  if (typeof cents !== "number" || isNaN(cents)) {
    return 0;
  }

  // Calculate the reduction: $2.25 (225 cents) + 0.25% of original amount
  const fixedReduction = 225; // $2.25 in cents
  const percentageReduction = cents * 0.0025; // 0.25% of original amount
  const totalReduction = fixedReduction + percentageReduction;

  // Apply the reduction
  const reducedAmount = cents - totalReduction;

  // Round to nearest cent and ensure it's not negative
  return Math.max(0, Math.round(reducedAmount));
};
